import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { CanvasStage } from "./canvas-stage";
import { ToolBar } from "./toolbar/tool-bar";
import { AppMenu } from "./controls/app-menu";
import { ObjectContextMenu } from "./controls/object-context-menu";
import { PresenceList } from "./controls/presence-list";
import { FocusModeOverlay } from "./focus-mode-overlay";
import { MiniMap } from "./minimap/mini-map";
import { NoticeHost } from "./notice/notice-host";
import { CommentLayer } from "./comments/comment-layer";
import { createCommentImageUploader } from "./comments/comment-image-upload";
import { ShortcutTipsPanel } from "../../components/shortcut-tips-panel";
import { useCanvasBootstrap } from "./use-canvas-bootstrap";
import { useFocusMode } from "./use-focus-mode";
import { useRealtimeConnection } from "../../realtime/use-realtime-connection";
import { useCanvasThumbnail } from "./use-canvas-thumbnail";
import { useTopRightReserve } from "./use-top-right-reserve";
import { useCanvasApi } from "../../lib/canvas-api";
import { useCanvasExportSnapshot } from "./use-canvas-export-snapshot";
import { RealtimeClientProvider } from "../../realtime/realtime-context";
import { LoadingOverlay } from "../../components/loading-overlay";
import {
  ErrorReloadCountdown,
  resetLoadErrorAutoReloadAttemptCount,
} from "../../components/error-reload-countdown";
import { useCurrentActorState, type CurrentActor } from "../../auth/use-current-actor";
import { startEmbedSearchListener } from "../../auth/embed-auth";
import { CanvasOpDispatcherProvider } from "./dispatcher-context";
import { useCanvasOpDispatcher } from "./use-canvas-op-dispatcher";
import { useShowToast } from "./notice/use-show-toast";
import { createStaticRealtimeClient } from "../../realtime/static-realtime";
import { useCanvasStore } from "./store/canvas-store";
import { useCanvasDocStore } from "./store/canvas-doc-store";
import { CanvasTimer } from "./timer/canvas-timer";
import { CanvasTimerFinishAlert } from "./timer/canvas-timer-finish-alert";
import { TrashedCanvasBanner } from "./controls/trashed-canvas-banner";
import {
  resolveCanvasLoadPresentation,
  useCanvasInitialLoadLatch,
} from "./use-canvas-initial-load-latch";

interface Props {
  readonly canvasId: string;
  /** 参照専用の埋め込みモード（Connect Note からの iframe）。true で編集 UI を持たないビューを描画する。 */
  readonly embed?: boolean | undefined;
  /**
   * 新規キャンバス作成時に採用する初期キャンバス名（URL クエリ ?name= 由来）。
   * 編集ビューの lazy create 時のみ使う。参照専用の埋め込みでは新規作成が起きないため無視する。
   */
  readonly createName?: string | undefined;
}

// /c/:canvasId 配下のビュー。通常は編集ビュー、`embed` のときは参照専用ビューを描画する。
export function CanvasView({ canvasId, embed = false, createName }: Props): JSX.Element {
  // キャンバスの切り替え時にロード状態と完了ラッチを必ず作り直す。
  return embed ? (
    <CanvasEmbedView key={canvasId} canvasId={canvasId} />
  ) : (
    <CanvasEditorView key={canvasId} canvasId={canvasId} createName={createName} />
  );
}

// /c/:canvasId 配下の全画面レイアウト（編集ビュー）。
// - REST bootstrap でスナップショットを doc store にロード
// - WebSocket でサーバ op を購読
// - 子コンポーネントは RealtimeClientProvider 経由で sendOp を呼べる
//
// 存在しない canvasId でもクライアント側で空キャンバスとして開始する（lazy create）。
function CanvasEditorView({
  canvasId,
  createName,
}: {
  readonly canvasId: string;
  readonly createName?: string | undefined;
}): JSX.Element {
  // createName は未作成キャンバスの lazy create（bootstrap GET）時のみ初期名として採用される。
  const bootstrap = useCanvasBootstrap(canvasId, { createName });
  const realtime = useRealtimeConnection(canvasId);
  const actorState = useCurrentActorState();
  const thumbnailDataUrl = useCanvasThumbnail(canvasId);
  // 全体像 PDF スナップショットを約 20 秒のデバウンスで自動保存する（Connect Note 連携）。
  useCanvasExportSnapshot(canvasId);
  const loadState = buildCanvasLoadState({
    actorStatus: actorState.status,
    bootstrapStatus: bootstrap.status,
    realtimeStatus: realtime.status,
  });
  const initialLoadCompleted = useCanvasInitialLoadLatch(loadState.ready);
  const presentation = resolveCanvasLoadPresentation({
    initialLoadCompleted,
    error: loadState.error,
  });

  // 読み込みが完了したら、エラー時の自動リロード回数カウンタをリセットする。
  // 次にエラーが起きた時、また最初から自動リロードを試せるようにするため。
  useEffect(() => {
    if (loadState.ready) {
      resetLoadErrorAutoReloadAttemptCount();
    }
  }, [loadState.ready]);

  return (
    <div
      data-testid="canvas-root"
      style={{
        position: "fixed",
        inset: 0,
        overflow: "hidden",
        background: "var(--color-bg)",
      }}
    >
      {realtime.client && presentation.showWorkspace ? (
        <RealtimeClientProvider client={realtime.client}>
          <CanvasRealtimeWorkspace
            actor={actorState.actor}
            canvasId={canvasId}
            thumbnailDataUrl={thumbnailDataUrl}
          />
        </RealtimeClientProvider>
      ) : null}
      {presentation.showOverlay ? (
        <LoadingOverlay
          title={loadState.error ? "読み込みに失敗しました" : "キャンバスを読み込んでいます"}
          message={loadState.message}
          progress={loadState.progress}
          variant={loadState.error ? "error" : "loading"}
          footer={loadState.error ? <ErrorReloadCountdown /> : undefined}
        />
      ) : null}
    </div>
  );
}

interface CanvasRealtimeWorkspaceProps {
  readonly actor: CurrentActor;
  readonly canvasId: string;
  readonly thumbnailDataUrl: string | undefined;
}

function CanvasRealtimeWorkspace({
  actor,
  canvasId,
  thumbnailDataUrl,
}: CanvasRealtimeWorkspaceProps): JSX.Element {
  const canvasApi = useCanvasApi();
  // 管理者判定（コメントの編集・削除ボタンの表示可否などのクライアント側ガードに使う。
  // 書き込みの最終判定はサーバ側）。dashboard と同じ /me クエリを再利用する。
  const meQuery = useQuery({ queryKey: ["me"], queryFn: canvasApi.fetchMe });
  const canAdmin = meQuery.data?.actor.permissions.canAdmin ?? false;
  const canWrite = meQuery.data?.actor.permissions.canWrite ?? false;
  const isTrashed = useCanvasDocStore((state) => state.meta.isTrashed ?? false);
  const showToast = useShowToast();

  const dispatcher = useCanvasOpDispatcher({
    actorUserId: actor.userId,
    actorDisplayName: actor.displayName,
    actorAvatarImageUrl: actor.avatarImageUrl,
    actorCanAdmin: canAdmin,
    onTableCellsClamped: () => {
      showToast("warning", "表の内容が送信できる長さに切り詰められました。");
    },
  });
  // フォーカスモードを有効化（対象が居る間だけ追尾し、利用者の能動操作で自動解除する）。
  useFocusMode();
  // コメントへの画像貼り付け用アップローダ。画像オブジェクトと同じ添付基盤を使う
  // （貼り付け時に pending へ PUT し、送信確定時に attachments へ昇格する二段階）。
  const commentImageUploader = useMemo(
    () => createCommentImageUploader(canvasApi, canvasId),
    [canvasApi, canvasId]
  );
  // 右上コントロールの実測幅を CSS 変数へ反映し、左上アプリメニューの折り返し幅の
  // 差し引きに使う（画面幅に余裕があるのに折り返すのを防ぐ）。
  const topRightControlsRef = useRef<HTMLDivElement | null>(null);
  useTopRightReserve(topRightControlsRef);

  // AppMenu や選択オーバーレイなど、キャンバス画面全体で同じ dispatcher を共有する。
  return (
    <CanvasOpDispatcherProvider value={dispatcher}>
      <CanvasStage canvasId={canvasId} currentUserId={actor.userId} dispatcher={dispatcher} />
      <CommentLayer
        currentUserId={actor.userId}
        currentUserName={actor.displayName}
        currentUserAvatar={actor.avatarImageUrl}
        canAdmin={canAdmin}
        commentImageUploader={commentImageUploader}
        readOnly={isTrashed}
      />
      <AppMenu canvasId={canvasId} />
      <div ref={topRightControlsRef} className="canvas-top-right-controls">
        <PresenceList />
        <CanvasTimer />
      </div>
      <ToolBar />
      <MiniMap thumbnailDataUrl={thumbnailDataUrl} />
      <TrashedCanvasBanner canvasId={canvasId} canWrite={canWrite} />
      <NoticeHost />
      <ShortcutTipsPanel />
      <ObjectContextMenu />
      <CanvasTimerFinishAlert />
      <FocusModeOverlay />
    </CanvasOpDispatcherProvider>
  );
}

// 参照専用の埋め込みビュー。ロード時点のスナップショット（REST bootstrap）のみを表示し、
// WebSocket 接続・サムネ生成・全体像 PDF 自動保存は行わない（フックを呼ばない）。
function CanvasEmbedView({ canvasId }: { readonly canvasId: string }): JSX.Element {
  // 埋め込みでは取得失敗を空キャンバス成功に変換しない（401/403/404/500 を空表示で握りつぶさない）。
  const bootstrap = useCanvasBootstrap(canvasId, { lazyCreateOnError: false });
  const actorState = useCurrentActorState();
  // 埋め込みは WS 非接続。realtime ステップは「完了」とみなして読み込みを確定させる。
  const loadState = buildCanvasLoadState({
    actorStatus: actorState.status,
    bootstrapStatus: bootstrap.status,
    realtimeStatus: "connected",
  });
  const initialLoadCompleted = useCanvasInitialLoadLatch(loadState.ready);
  // ディスパッチャ / カーソルブロードキャスタが provider を前提とするため、静的（no-op）クライアントを渡す。
  const staticClient = useMemo(() => createStaticRealtimeClient(), []);

  useEffect(() => {
    if (loadState.ready) {
      resetLoadErrorAutoReloadAttemptCount();
    }
  }, [loadState.ready]);

  // 取得失敗（権限切れ・未存在・トークン失効・サーバエラー）は、空キャンバスや自動リロードではなく、
  // iframe 内でログイン画面へ遷移させない静的な「表示できませんでした」表示にする（利用者要件）。
  if (loadState.error) {
    return <CanvasEmbedUnavailablePane />;
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        overflow: "hidden",
        background: "var(--color-bg)",
      }}
    >
      {initialLoadCompleted ? (
        <RealtimeClientProvider client={staticClient}>
          <CanvasEmbedWorkspace actor={actorState.actor} canvasId={canvasId} />
        </RealtimeClientProvider>
      ) : (
        <LoadingOverlay
          title="キャンバスを読み込んでいます"
          message={loadState.message}
          progress={loadState.progress}
          variant="loading"
        />
      )}
    </div>
  );
}

/**
 * 参照専用の埋め込みでキャンバスを表示できなかったときの静的ペイン。
 * ログイン画面へは遷移させず、Connect Note から開き直す案内と手動再読み込みだけを提供する
 * （自動リロードのループも避ける）。
 */
function CanvasEmbedUnavailablePane(): JSX.Element {
  return (
    <div className="app-bootstrap" role="alert" aria-live="polite">
      <div>
        <p>キャンバスを表示できませんでした。</p>
        <p>
          権限の期限切れや一時的な問題の可能性があります。Connect Note
          の記事画面から開き直してください。
        </p>
        <p>
          <button type="button" onClick={() => window.location.reload()}>
            再読み込みする
          </button>
        </p>
      </div>
    </div>
  );
}

interface CanvasEmbedWorkspaceProps {
  readonly actor: CurrentActor;
  readonly canvasId: string;
}

function CanvasEmbedWorkspace({ actor, canvasId }: CanvasEmbedWorkspaceProps): JSX.Element {
  const setReadOnly = useCanvasStore((s) => s.setReadOnly);
  const setSearchQuery = useCanvasStore((s) => s.setSearchQuery);
  // マウント中は参照専用フラグを立て、アンマウントで戻す（編集系フックが store 経由で無効化される）。
  // オブジェクトのドラッグ等（store の readOnly を参照する）が初回ペイント前に確実に無効化されるよう、
  // passive effect ではなく layout effect でペイント前に設定する。
  //
  // 親（Connect Note）からの検索語も、この参照専用ワークスペースが載っている間だけ受け取る。
  // 受信の購読を readOnly の設定と同じ効果に置くことで、「参照専用でだけ強調する」という条件が
  // 1 か所にまとまり、編集ビューでは受信経路そのものが設置されない。
  useLayoutEffect(() => {
    setReadOnly(true);
    const stopSearchListener = startEmbedSearchListener(setSearchQuery);
    return () => {
      stopSearchListener();
      // 参照専用を抜けるときは強調を必ず消す（setReadOnly(false) も空へ落とすが、順序に依らせない）。
      setSearchQuery("");
      setReadOnly(false);
    };
  }, [setReadOnly, setSearchQuery]);

  // 参照専用では書き込みは行わないが、CanvasStage / CommentLayer が dispatcher を要求するため用意する
  // （static realtime クライアントへ流れるだけで実際の送信はされない）。canAdmin は常に false。
  const dispatcher = useCanvasOpDispatcher({
    actorUserId: actor.userId,
    actorDisplayName: actor.displayName,
    actorAvatarImageUrl: actor.avatarImageUrl,
    actorCanAdmin: false,
  });

  // ツールバー・メニュー・ミニマップ・ショートカット案内・コンテキストメニュー・フォーカスモードは
  // 描画しない。パン / ズーム可能な CanvasStage と、閲覧専用の CommentLayer のみを描画する。
  return (
    <CanvasOpDispatcherProvider value={dispatcher}>
      <CanvasStage
        canvasId={canvasId}
        currentUserId={actor.userId}
        dispatcher={dispatcher}
        readOnly
      />
      <CommentLayer
        currentUserId={actor.userId}
        currentUserName={actor.displayName}
        currentUserAvatar={actor.avatarImageUrl}
        canAdmin={false}
        readOnly
      />
    </CanvasOpDispatcherProvider>
  );
}

interface CanvasLoadInput {
  readonly actorStatus: "loading" | "ready" | "error";
  readonly bootstrapStatus: "loading" | "ready" | "error";
  readonly realtimeStatus: "idle" | "connecting" | "connected" | "error";
}

function buildCanvasLoadState(input: CanvasLoadInput): {
  readonly ready: boolean;
  readonly error: boolean;
  readonly message: string;
  readonly progress: number;
} {
  if (input.actorStatus === "error") {
    return {
      ready: false,
      error: true,
      message: "利用者情報の取得に失敗しました。",
      progress: 100,
    };
  }
  if (input.bootstrapStatus === "error") {
    // 埋め込みで lazyCreateOnError=false のとき、取得失敗（権限切れ・未存在・トークン失効・サーバ
    // エラー）を空キャンバス成功に変換せず error として扱う（通常編集画面ではこの状態にはならない）。
    return {
      ready: false,
      error: true,
      message: "キャンバスを表示できませんでした。",
      progress: 100,
    };
  }
  if (input.realtimeStatus === "error") {
    return {
      ready: false,
      error: true,
      message: "共同編集接続に失敗しました。",
      progress: 100,
    };
  }

  const steps = [
    { done: input.actorStatus === "ready", message: "利用者情報を確認しています。" },
    { done: input.bootstrapStatus === "ready", message: "キャンバスデータを読み込んでいます。" },
    { done: input.realtimeStatus === "connected", message: "共同編集接続を準備しています。" },
  ] as const;
  const doneCount = steps.filter((step) => step.done).length;
  const next = steps.find((step) => !step.done);
  const ready = doneCount === steps.length;

  return {
    ready,
    error: false,
    message: next?.message ?? "表示を準備しています。",
    progress: ready ? 100 : Math.max(12, Math.round((doneCount / steps.length) * 100)),
  };
}
